/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/emailFlowErrors.scss';

import React, {ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useMemo, useState} from "react";
import {EmailStepError} from "../../util/types";
import {capitalize, WithBackground} from "@dvrd/dvr-controls";
import {BolmateButton} from "../controls";
import {translateEmailStepError} from "../../util/bolValidation";
import {ActivateHandle} from "../../util/interfaces";
import WarnIcon from "../icon/warnIcon";
import ErrorIcon from "../icon/errorIcon";

function EmailFlowErrors(_: {}, ref: ForwardedRef<ActivateHandle>) {
    const [active, setActive] = useState(false);
    const [errors, setErrors] = useState<Array<EmailStepError>>([]);
    const [action, setAction] = useState('');

    function onActivate(errors: Array<EmailStepError>, action: string) {
        setErrors(errors);
        setAction(action);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setErrors([]);
        setAction('');
    }

    const groupedErrors: Record<string, Array<EmailStepError>> = useMemo(() => {
        const grouped: Record<string, Array<EmailStepError>> = {};
        for (const error of errors) {
            let stepLabel: string;
            if (error.reason === 'overlap') stepLabel = error.step1_label;
            else if (error.reason === 'content') stepLabel = error.step_label;
            else continue;
            if (!grouped.hasOwnProperty(stepLabel)) grouped[stepLabel] = [];
            grouped[stepLabel].push(error);
        }
        return grouped;
    }, [errors]);

    function renderBody() {
        return (
            <div className='body-container'>
                <p className='error-message'>Het {action} is niet gelukt doordat de volgende problemen gevonden
                    zijn:</p>
                {Object.entries(groupedErrors).map(renderErrors)}
            </div>
        )
    }

    function renderErrors([label, errors]: [string, Array<EmailStepError>], index: number) {
        return (
            <div key={index} className='step-error'>
                <label className='step-label'>{label}</label>
                {errors.map(renderError)}
            </div>
        )
    }

    function renderError(error: EmailStepError, index: number) {
        let icon: ReactNode = null;
        if (error.reason === 'content') icon = <WarnIcon/>;
        else if (error.reason === 'overlap') icon = <ErrorIcon/>
        return <label key={index} className='error-label'>{icon} {translateEmailStepError(error, true)}</label>
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <WithBackground onClose={onClose} active={active}>
            <div className='default-popup email-step-errors'>
                <div className='header-container'>
                    <label className='header-title'>{capitalize(action)} mislukt</label>
                </div>
                {renderBody()}
                <div className='footer-container btn'>
                    <BolmateButton onClick={onClose} label='Oké'/>
                </div>
            </div>
        </WithBackground>
    )
}

export default forwardRef<ActivateHandle, {}>(EmailFlowErrors);